import React, {ReactNode} from 'react'
import styled from 'styled-components'
import {FontWeights} from '../../../../styledComponents/Constants'
import { Box, TableBody, TableRow, TableCell, TableHead } from '@mui/material';
import {HOURS, HOURS_24, UNIT_OF_BUSINESS_SCHEDULE, VALID_OR_NOT} from '../../../../CodeMaster'
import {TimeTableGroup, TimeTableItem, TimeTableTime} from './BusinessScheduleItemInterfaces'
import {createHourArray} from './BusinessScheduleTimeHelper'
import {Nullable} from '../../../../app/TypeHelpers'
import TimeTableViewTimeBox from './TimeTableViewTimeBox'
import TimeTableViewBackground from './TimeTableViewBackground'
import TimeTableViewItem from './TimeTableViewItem'
import equal from 'fast-deep-equal'

interface Props {
    groupItems: TimeTableGroup[]
    items: TimeTableItem[]
    defaultTimeStart: TimeTableTime<HOURS_24>
    defaultTimeEnd: TimeTableTime<HOURS | HOURS_24>
    pixelPer5Minutes: number
}

interface GroupItemTime {
    hour: number
    minute: number
}

interface GroupItem {
    startTime: GroupItemTime
    endTime: GroupItemTime
    id: string
    title: string
}

const TimeTableView: React.FC<Props> = React.memo(props => {

    const pixelPer1Minute = props.pixelPer5Minutes / 5

    const renderGroupHeaderRow = (): ReactNode => {
        const {groupItems} = props

        return (
            <TableRow style={{height: 'fit-content'}}>
                <NoPaddingTableCell/>
                {
                    groupItems.map(group => (
                        <GroupHeaderCell
                            groupcount={groupItems.length}
                            align={'center'}
                            key={group.businessScheduleUnit.value}
                        >
                            {group.title}
                        </GroupHeaderCell>
                    ))
                }
            </TableRow>
        )
    }

    const renderGroupColumn = (
        businessScheduleUnit: UNIT_OF_BUSINESS_SCHEDULE, hours: number[], items: TimeTableItem[],
    ): ReactNode => {

        const timeTableItems: TimeTableItem[] = items
            .filter(item => {
                return item.businessScheduleUnit === businessScheduleUnit
                    || item.businessScheduleUnit === UNIT_OF_BUSINESS_SCHEDULE.ALL_DAYS
            })

        const groupItems: GroupItem[] = splitScheduleIfDateStraddleIsValid(timeTableItems)
        return (
            <TimeTableCell key={businessScheduleUnit.value}>
                <TimeTableViewBackground
                    businessScheduleUnit={businessScheduleUnit}
                    hours={hours}
                    pixelPer1Minute={pixelPer1Minute}
                />

                {
                    groupItems.map((item, index) => {
                        const {id, title} = item
                        return (
                            <TimeTableViewItem
                                key={`${businessScheduleUnit.value}-${id}-${index}`}
                                pixelPer5Minutes={props.pixelPer5Minutes}
                                {...item}
                            >
                                {title}
                            </TimeTableViewItem>
                        )
                    })
                }
            </TimeTableCell>
        )
    }

    const hoursArray: number[] = createHourArray(props.defaultTimeStart, props.defaultTimeEnd)

    const renderGroupColumns = (): ReactNode => {
        return props.groupItems
            .map(group => renderGroupColumn(group.businessScheduleUnit, hoursArray, props.items))
    }

    const timeTableViewHeight = (hoursArray.length - 1) * 60
    return (
        <Box padding={'20px 5px 5px 5px'}>
            <FullSizeTable>
                <TableHead>
                    {renderGroupHeaderRow()}
                </TableHead>
                <TableBody>
                    <TableRow style={{height: `calc(${timeTableViewHeight}px/5*${props.pixelPer5Minutes})`}}>
                        <NoPaddingTableCell>
                            <TimeTableViewTimeBox
                                pixelPer1Minute={pixelPer1Minute}
                                hours={hoursArray}
                            />
                        </NoPaddingTableCell>
                        {renderGroupColumns()}
                    </TableRow>
                </TableBody>
            </FullSizeTable>
        </Box>
    )
}, (prevProps, nextProps) => (prevProps === nextProps || equal(prevProps, nextProps)))

const convertTimeTableTime2GroupTime = (time: Nullable<TimeTableTime<HOURS_24>>): GroupItemTime => {
    if (time === null) {
        return {hour: 0, minute: 0}
    }

    return {
        hour: Number(time.hour.value),
        minute: Number(time.minute.value),
    }
}

const convertTimeTableItem2GroupItem = (item: TimeTableItem): GroupItem => {
    const {id, title, startTime, endTime, joinPrevDaysSchedule} = item

    let convertedStartTime: GroupItemTime
    if (joinPrevDaysSchedule === VALID_OR_NOT.VALID) {
        convertedStartTime = {hour: 0, minute: -30}
    } else {
        convertedStartTime = convertTimeTableTime2GroupTime(startTime)
    }
    return {
        id: id,
        title: title,
        startTime: convertedStartTime,
        endTime: convertTimeTableTime2GroupTime(endTime),
    }
}

const splitScheduleIfDateStraddleIsValid = (items: TimeTableItem[]): GroupItem[] => {
    const someOfItemIsDateStraddle = items.some(item => item.dateStraddle === VALID_OR_NOT.VALID)
    if (!someOfItemIsDateStraddle) {
        return items.map(convertTimeTableItem2GroupItem)
    }

    const splitDateStraddleItem = (item: TimeTableItem): GroupItem[] => {
        if (item.dateStraddle !== VALID_OR_NOT.VALID) {
            return [convertTimeTableItem2GroupItem(item)]
        }

        return [
            {
                id: item.id,
                title: item.title,
                startTime: convertTimeTableTime2GroupTime(item.startTime),
                endTime: {hour: 24, minute: 30},
            },
            {
                id: item.id,
                title: item.title,
                startTime: {hour: 0, minute: -30},
                endTime: convertTimeTableTime2GroupTime(item.endTime),
            },
        ]
    }

    let newSplittedTimeTableItems: GroupItem[] = []
    for (const item of items) {
        const splittedItems = splitDateStraddleItem(item)
        newSplittedTimeTableItems = [...newSplittedTimeTableItems, ...splittedItems]
    }
    return newSplittedTimeTableItems
}


const FullSizeTable = styled.table`
  width: 100%;
  height: 100%;
`

const NoPaddingTableCell = styled(TableCell)`
  padding: 0 0 0 3px !important;
  border-bottom: none!important;
`
const GroupHeaderCell = styled(NoPaddingTableCell)<{groupcount: number}>`
  color: dimgray;
  font-weight: ${FontWeights.Bold}!important;
  ${props =>
        `width: ${100 / props.groupcount}%;`
    }
`
const TimeTableCell = styled(NoPaddingTableCell)`
  vertical-align: top!important;
  position: relative;
`

TimeTableView.displayName = 'TimeTableView'
export default TimeTableView
